import { useCallback, useEffect, useState } from 'react';
import { mailSettings, type VisitMailRecipients } from '../api/mail';
import { IconCircleAlert, IconMail, IconPlus, IconRefreshCw, IconTrash2 } from '../icons';
import { describeError, useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { Button, IconButton } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { Alert, Field, Select, TextInput } from '../ui/Field';
import { Card, PageHeader } from '../ui/Page';

/**
 * Mail Gönderim Ayarları — who is told when a visit of a given kind is handed in.
 *
 * <p>One list per visit type, To and Cc side by side, exactly as the customer works today.
 * A visit type with no addresses sends nothing, which is the right default: a mail nobody
 * asked for, going to a half-filled list, is how an audit system teaches people to filter
 * it out.</p>
 */
export function MailSettings() {
  const t = useTranslate();

  const [lists, setLists] = useState<VisitMailRecipients[]>([]);
  const [chosen, setChosen] = useState<string>('');

  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);

  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = await mailSettings.list();
      setLists(all);
      setFailed(false);
      setError(null);

      // Whatever was being looked at, or the first visit type. Keeping the choice across a
      // reload matters: saving reloads, and landing back on a different list every time is
      // how somebody edits the wrong one.
      setChosen((current) => (all.some((x) => x.visitTypeId === current) ? current : all[0]?.visitTypeId ?? ''));
    } catch (e) {
      setFailed(true);
      setError(describeError(e, 'error.listFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Closing or reloading the tab with an unsaved list asks first. Moving to another screen
  // inside the app does not: the router here has no blocker (field test, 2 November).
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // The chosen list's addresses, whenever the choice or the data changes. Unsaved edits are
  // deliberately dropped when the visit type changes — carrying them to another list would
  // be worse than losing them.
  useEffect(() => {
    const list = lists.find((x) => x.visitTypeId === chosen);

    setTo(list?.to ?? []);
    setCc(list?.cc ?? []);
    setDirty(false);
  }, [chosen, lists]);

  // The "saved" note belongs to the list it was saved on. It used to be cleared above, on
  // the reload that every save does, so it never showed at all (field test, 3 November).
  useEffect(() => setSaved(false), [chosen]);

  async function save() {
    if (saving || !chosen) return;

    setSaving(true);
    try {
      await mailSettings.save(chosen, { to, cc });
      setError(null);
      setSaved(true);
      await load();
    } catch (e) {
      setError(describeError(e, 'error.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  const current = lists.find((x) => x.visitTypeId === chosen);

  if (failed) {
    return (
      <>
        <PageHeader crumbs={[t('app.definitions')]} title={t('mail.title')} />
        <Card>
          <EmptyState
            icon={IconCircleAlert}
            tone="danger"
            title={t('common.loadFailed')}
            text={t('common.loadFailedText')}
            action={<Button icon={IconRefreshCw} onClick={() => void load()}>{t('common.retry')}</Button>}
          />
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        crumbs={[t('app.definitions')]}
        title={t('mail.title')}
        subtitle={t('mail.subtitle')}
        // In the header, where System Parameters and Map Indicators keep theirs. It sat at
        // the bottom left here, the one screen that did (field test, 2 November).
        action={
          current ? (
            <div className="nv-actions-bar">
              {saved && !dirty ? <Badge tone="comment">{t('mail.saved')}</Badge> : null}
              {dirty ? <Badge tone="na">{t('mail.unsaved')}</Badge> : null}
              <Button variant="primary" disabled={!dirty || saving} onClick={() => void save()}>
                {t('common.save')}
              </Button>
            </div>
          ) : undefined
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      <Card className="nv-pad">
        <Field label={t('mail.visitType')} hint={t('mail.visitTypeHint')}>
          {({ id }) => (
            <Select
              id={id}
              value={chosen}
              disabled={loading || lists.length === 0}
              onChange={(e) => setChosen(e.target.value)}
            >
              {lists.map((list) => (
                <option key={list.visitTypeId} value={list.visitTypeId}>
                  {list.visitTypeName}
                  {list.to.length + list.cc.length > 0 ? ` (${list.to.length + list.cc.length})` : ''}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {lists.length === 0 && !loading ? (
          <EmptyState icon={IconMail} title={t('mail.noVisitTypes')} text={t('mail.noVisitTypesText')} />
        ) : null}
      </Card>

      {current ? (
        <>
          <div className="nv-mail-lists">
            <AddressList
              title={t('mail.to')}
              hint={t('mail.toHint')}
              addresses={to}
              onChange={(next) => {
                setTo(next);
                setDirty(true);
                setSaved(false);
              }}
            />

            <AddressList
              title={t('mail.cc')}
              hint={t('mail.ccHint')}
              addresses={cc}
              onChange={(next) => {
                setCc(next);
                setDirty(true);
                setSaved(false);
              }}
            />
          </div>

          {to.length === 0 && cc.length === 0 ? (
            // Said plainly rather than left to be inferred from two empty boxes. An
            // address list that silently sends nothing is the one somebody assumes works.
            <span className="nv-hint">{t('mail.sendsNothing')}</span>
          ) : null}
        </>
      ) : null}
    </>
  );
}

/** Enough to catch a typo or a name pasted into the box; the server has the final say. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** One list of addresses, added one at a time and removed one at a time. */
function AddressList({
  title,
  hint,
  addresses,
  onChange,
}: {
  title: string;
  hint: string;
  addresses: string[];
  onChange: (addresses: string[]) => void;
}) {
  const t = useTranslate();
  const [typed, setTyped] = useState('');
  const [invalid, setInvalid] = useState(false);

  function add() {
    const address = typed.trim();
    if (address.length === 0) return;

    // Checked here, at the box. It used to go into the list and be refused only at Save,
    // taking the good addresses down with it (field test, 2 November).
    if (!EMAIL.test(address)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);

    // Case-insensitively: the same address typed twice is one address, and a list that
    // holds both sends the mail twice.
    if (!addresses.some((a) => a.toLowerCase() === address.toLowerCase())) {
      onChange([...addresses, address]);
    }

    setTyped('');
  }

  return (
    <Card className="nv-pad">
      <h3 className="nv-h3">{title}</h3>
      <p className="nv-hint">{hint}</p>

      <div className="nv-mail-add">
        <TextInput
          type="email"
          value={typed}
          placeholder={t('mail.placeholder')}
          aria-invalid={invalid || undefined}
          onChange={(e) => {
            setTyped(e.target.value);
            setInvalid(false);
          }}
          onKeyDown={(e) => {
            // Enter adds, because this is a box somebody pastes into six times in a row and
            // reaching for the mouse between each is the difference between a list being
            // kept up to date and not.
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button icon={IconPlus} onClick={add} disabled={typed.trim().length === 0}>
          {t('mail.add')}
        </Button>
      </div>

      {invalid ? <p className="nv-field__error" role="alert">{t('mail.invalid', { address: typed.trim() })}</p> : null}

      {addresses.length === 0 ? (
        <p className="nv-subtle">{t('mail.none')}</p>
      ) : (
        <ul className="nv-mail-list">
          {addresses.map((address) => (
            <li key={address}>
              <span>{address}</span>
              <IconButton
                label={t('common.delete')}
                icon={IconTrash2}
                tone="danger"
                onClick={() => onChange(addresses.filter((a) => a !== address))}
              />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
